import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { checkAuthBeforeAction } from '../utils/authHelper';
import { userAPI } from '../services/api';

const Profile = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [stats, setStats] = useState({
    totalGenerations: 0,
    generationsUsed: 0,
    generationsLimit: 0,
    accountCreated: '',
    lastLogin: '',
    planName: '',
    planStatus: '',
    periodEnd: '',
    cancelAtPeriodEnd: false
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar_url || '');

  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Check if user is OAuth user (Google, etc.)
  const isOAuthUser = user?.oauth_provider !== null && user?.oauth_provider !== undefined;

  // Load profile data on mount
  useEffect(() => {
    loadProfileData();
  }, []);

  // Update form when user data changes
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        full_name: user.full_name || '',
        email: user.email || ''
      }));
      setAvatarPreview(user.avatar_url || '');
      setStats(prev => ({
        ...prev,
        accountCreated: user.created_at ? new Date(user.created_at).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        }) : '',
        lastLogin: user.last_login_at ? new Date(user.last_login_at).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        }) : '',
        planName: user.subscription?.plan_name || 'Free',
        planStatus: user.subscription?.status || 'active',
        periodEnd: user.subscription?.current_period_end ? new Date(user.subscription.current_period_end).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        }) : '',
        cancelAtPeriodEnd: user.subscription?.cancel_at_period_end || false
      }));
    }
  }, [user]);

  const loadProfileData = async () => {
    try {
      // Load usage statistics
      const usageResponse = await userAPI.getUsage();
      if (usageResponse.data.success) {
        const { current, limit, byContentType } = usageResponse.data.data;
        const total = byContentType.reduce((sum, item) => sum + parseInt(item.count), 0);
        setStats(prev => ({
          ...prev,
          totalGenerations: total,
          generationsUsed: current || 0,
          generationsLimit: limit || 0
        }));
      }
    } catch (error) {
      console.error('Failed to load profile data:', error);
      // Error is acceptable - user can still view the page structure
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
        setMessage({
          type: 'error',
          text: `Avatar file size is ${fileSizeMB}MB. Maximum allowed size is 5MB. Please choose a smaller image.`
        });
        // Reset file input
        e.target.value = '';
        setAvatarFile(null);
        return;
      }

      // Check file type
      if (!file.type.startsWith('image/')) {
        setMessage({
          type: 'error',
          text: 'Please select a valid image file (JPG, PNG, or GIF)'
        });
        e.target.value = '';
        setAvatarFile(null);
        return;
      }

      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
      // Clear any previous error messages
      setMessage({ type: '', text: '' });
    }
  };

  const handleAvatarUpload = async () => {
    if (!avatarFile) return;

    // Check authentication before making API call
    if (!checkAuthBeforeAction(navigate)) {
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const formData = new FormData();
      formData.append('avatar', avatarFile);

      const response = await userAPI.uploadAvatar(formData);

      if (response.data.success) {
        setMessage({ type: 'success', text: 'Avatar updated successfully' });
        updateUser({ ...user, avatar_url: response.data.data.avatar_url });
        setAvatarFile(null);
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error?.message || 'Failed to upload avatar'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check authentication before making API call
    if (!checkAuthBeforeAction(navigate)) {
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await userAPI.updateProfile({
        full_name: formData.full_name
      });

      if (response.data.success) {
        setMessage({ type: 'success', text: 'Profile updated successfully' });
        updateUser({ ...user, full_name: formData.full_name });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error?.message || 'Failed to update profile'
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    // Check authentication before making API call
    if (!checkAuthBeforeAction(navigate)) {
      return;
    }

    // Validation
    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      setMessage({ type: 'error', text: 'All password fields are required' });
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (formData.newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters long' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await userAPI.changePassword({
        current_password: formData.currentPassword,
        new_password: formData.newPassword
      });

      if (response.data.success) {
        setMessage({ type: 'success', text: 'Password changed successfully' });
        setFormData(prev => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }));
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error?.message || 'Failed to change password'
      });
    } finally {
      setLoading(false);
    }
  };

  // Get plan color scheme based on plan name
  const getPlanColorScheme = (planName) => {
    const plan = planName?.toLowerCase() || '';

    if (plan.includes('basic')) {
      // 淡金黄色 - Basic plan
      return {
        gradient: 'from-yellow-50 to-amber-100',
        border: 'border-yellow-200',
        labelText: 'text-amber-600',
        titleText: 'text-amber-900',
        infoText: 'text-amber-700',
        infoTextBold: 'text-amber-900',
        freeText: 'text-amber-600'
      };
    } else if (plan.includes('professional') || plan.includes('pro')) {
      // 淡紫色 - Professional plan
      return {
        gradient: 'from-purple-50 to-purple-100',
        border: 'border-purple-200',
        labelText: 'text-purple-600',
        titleText: 'text-purple-900',
        infoText: 'text-purple-700',
        infoTextBold: 'text-purple-900',
        freeText: 'text-purple-600'
      };
    } else {
      // 淡蓝色 - Free plan (default)
      return {
        gradient: 'from-blue-50 to-blue-100',
        border: 'border-blue-200',
        labelText: 'text-blue-600',
        titleText: 'text-blue-900',
        infoText: 'text-blue-700',
        infoTextBold: 'text-blue-900',
        freeText: 'text-blue-600'
      };
    }
  };

  const planColors = getPlanColorScheme(stats.planName);

  // Helper function to get full avatar URL
  const getAvatarUrl = (avatarPath) => {
    if (!avatarPath) return '';
    // If it's already a full URL (including blob URLs), return as is
    if (avatarPath.startsWith('http://') || avatarPath.startsWith('https://') || avatarPath.startsWith('blob:')) {
      return avatarPath;
    }
    // If it's a relative path, prepend API domain (without /api path)
    // REACT_APP_API_URL is "https://api.getaizest.com/api", we need just the domain
    const API_URL = process.env.REACT_APP_API_URL || 'https://api.getaizest.com/api';
    const baseUrl = API_URL.endsWith('/api') ? API_URL.slice(0, -4) : API_URL;
    return `${baseUrl}${avatarPath}`;
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />

        <div className="flex-1 overflow-y-auto">
          <div className="max-w-5xl mx-auto px-6 py-8">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900">Profile Settings</h1>
              <p className="mt-2 text-gray-600">Manage your account settings and preferences</p>
            </div>

            {/* Message Alert */}
            {message.text && (
              <div className={`mb-6 p-4 rounded-lg ${
                message.type === 'success'
                  ? 'bg-green-50 border border-green-200 text-green-800'
                  : 'bg-red-50 border border-red-200 text-red-800'
              }`}>
                <div className="flex items-center">
                  {message.type === 'success' ? (
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
                    </svg>
                  )}
                  <span>{message.text}</span>
                </div>
              </div>
            )}

            <div className="space-y-6">
              {/* Avatar Section */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Profile Picture</h2>
                <div className="flex items-center space-x-6">
                  <div className="relative">
                    {avatarPreview ? (
                      <img
                        src={getAvatarUrl(avatarPreview)}
                        alt="Avatar"
                        className="w-24 h-24 rounded-full object-cover border-4 border-gray-100"
                        onError={(e) => {
                          console.error('Failed to load avatar:', getAvatarUrl(avatarPreview));
                          e.target.style.display = 'none';
                          // Reset avatarPreview to show default avatar
                          setAvatarPreview('');
                        }}
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center border-4 border-gray-100">
                        <span className="text-3xl font-bold text-white">
                          {user?.full_name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-4">
                      <label className="cursor-pointer">
                        <span className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition inline-block">
                          Choose File
                        </span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleAvatarChange}
                          className="hidden"
                        />
                      </label>
                      {avatarFile && (
                        <button
                          onClick={handleAvatarUpload}
                          disabled={loading}
                          className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition disabled:opacity-50"
                        >
                          {loading ? 'Uploading...' : 'Upload'}
                        </button>
                      )}
                    </div>
                    <p className="mt-2 text-sm text-gray-500">
                      JPG, PNG or GIF (max 5MB)
                    </p>
                  </div>
                </div>
              </div>

              {/* Profile Information */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Personal Information</h2>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name
                      </label>
                      <input
                        type="text"
                        name="full_name"
                        value={formData.full_name}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
                        disabled={isOAuthUser}
                        placeholder="Enter your full name"
                      />
                      {isOAuthUser && (
                        <p className="mt-1.5 text-xs text-gray-500">Name is managed by your Google account</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                        disabled
                      />
                      <p className="mt-1.5 text-xs text-gray-500">Email cannot be changed</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Account Created
                      </label>
                      <input
                        type="text"
                        value={stats.accountCreated}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                        disabled
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Login
                      </label>
                      <input
                        type="text"
                        value={stats.lastLogin || 'Never'}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                        disabled
                      />
                    </div>
                  </div>

                  {!isOAuthUser && (
                    <div className="pt-2">
                      <button
                        type="submit"
                        disabled={loading}
                        className="px-6 py-2.5 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Updating...' : 'Update Profile'}
                      </button>
                    </div>
                  )}
                </form>
              </div>

              {/* Change Password - Only for email/password users */}
              {!isOAuthUser && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">Change Password</h2>
                  <form onSubmit={handlePasswordChange} className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Current Password
                      </label>
                      <input
                        type="password"
                        name="currentPassword"
                        value={formData.currentPassword}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
                        placeholder="Enter current password"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          New Password
                        </label>
                        <input
                          type="password"
                          name="newPassword"
                          value={formData.newPassword}
                          onChange={handleChange}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
                          placeholder="Enter new password"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Confirm New Password
                        </label>
                        <input
                          type="password"
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
                          placeholder="Confirm new password"
                        />
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-600">
                        Password must be at least 8 characters long
                      </p>
                    </div>

                    <div className="pt-2">
                      <button
                        type="submit"
                        disabled={loading}
                        className="px-6 py-2.5 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Changing Password...' : 'Change Password'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Login Method Info - For OAuth users */}
              {isOAuthUser && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">Login Method</h2>
                  <div className="flex items-center space-x-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="bg-white rounded-lg p-3 shadow-sm">
                      <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-900">Connected with Google</p>
                      <p className="text-sm text-gray-600 mt-0.5">Your account is managed by Google. Password management is handled through your Google account settings.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Subscription Plan Details */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Subscription & Statistics</h2>

                {/* Plan Info Card - Full Width - Dynamic Colors */}
                <div className={`bg-gradient-to-br ${planColors.gradient} rounded-lg p-6 border ${planColors.border} mb-6`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={`text-sm ${planColors.labelText} font-medium mb-2`}>Current Plan</p>
                      <p className={`text-4xl font-bold ${planColors.titleText} mb-3`}>{stats.planName}</p>

                      {stats.periodEnd && (
                        <div className="mt-3">
                          <p className={`text-sm ${planColors.infoText} font-medium`}>
                            {stats.cancelAtPeriodEnd ? 'Expires on' : 'Renews on'}:
                            <span className={`ml-2 ${planColors.infoTextBold} font-semibold`}>{stats.periodEnd}</span>
                          </p>
                          {stats.cancelAtPeriodEnd && (
                            <p className="text-sm text-red-600 mt-2 flex items-center">
                              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                              </svg>
                              Subscription will not renew
                            </p>
                          )}
                        </div>
                      )}

                      {!stats.periodEnd && stats.planName === 'Free' && (
                        <p className={`text-sm ${planColors.freeText} mt-2`}>No expiration - Free plan</p>
                      )}
                    </div>

                    <div className={`px-4 py-2 rounded-full text-sm font-semibold ${
                      stats.planStatus === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {stats.planStatus === 'active' ? 'Active' : stats.planStatus}
                    </div>
                  </div>
                </div>

                {/* Additional Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-5 border border-green-200">
                    <p className="text-sm text-green-600 font-medium mb-2">Total Generations</p>
                    <p className="text-3xl font-bold text-green-900">{stats.totalGenerations}</p>
                    <p className="text-xs text-green-600 mt-1">All time</p>
                  </div>
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-5 border border-orange-200">
                    <p className="text-sm text-orange-600 font-medium mb-2">Account Age</p>
                    <p className="text-3xl font-bold text-orange-900">
                      {stats.accountCreated && new Date(user?.created_at) ?
                        Math.floor((new Date() - new Date(user.created_at)) / (1000 * 60 * 60 * 24)) : 0
                      } days
                    </p>
                    <p className="text-xs text-orange-600 mt-1">Since {stats.accountCreated}</p>
                  </div>
                </div>
              </div>

              {/* Email Verification Status */}
              {user && !user.is_email_verified && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                    </svg>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-yellow-800">Email not verified</p>
                      <p className="text-sm text-yellow-700 mt-1">Please check your email to verify your account.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
